import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";

/**
 * The Connections panel (`INT-11`). Doctrine: `docs/property-link.md` §6.
 *
 * ## What it replaces, and why the shape matters
 *
 * The old PMS card offered a HARDCODED list of five vendors — Mews, Opera,
 * Protel, Apaleo, Cloudbeds — while the folio seam knew only three. Selecting
 * Protel stored a free string no seam could act on and produced **no refusal at
 * all**, which is exactly the failure `folioPostingProviders.ts` exists to
 * prevent: somebody selects a vendor and assumes charges are flowing.
 *
 * So this component has **no vendor list of its own.** Vendors, their config
 * fields, their credential fields and their capability chips all come from
 * `GET /link/adapters`, which serves the adapter registry. Adding an adapter is
 * one server file and never touches this one — that is the epic's success
 * criterion, and this component is half of what makes it true.
 *
 * ## It never claims more than it can prove
 *
 * "Test connection" performs a REAL read against the vendor and reports the
 * result by code. A green tick the provider has not earned would contradict this
 * codebase's own conventions — it reports `cleared:false` rather than claim an
 * unproven wipe, and returns `undefined` rather than a placeholder guest name.
 *
 * Credentials are write-only. A stored one shows field names and a masked hint
 * and can be replaced, never read back; there is no server route that would
 * return one.
 */

interface Capabilities {
  postCharge: boolean;
  reverseCharge: boolean;
  readStays: boolean;
  readMenu: boolean;
  injectOrder: boolean;
  authoriseCharge: boolean;
  postingKey: "reservation" | "room";
  chargeableFlag: boolean;
  demo: boolean;
}

interface FieldSpec {
  key: string;
  label: string;
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
  help?: string;
}

interface AdapterStatus {
  vendor: string;
  displayName: string;
  capabilities: Capabilities;
  operational: boolean;
  reason: string | null;
  configFields?: FieldSpec[];
  credentialFields?: FieldSpec[];
}

interface Connection {
  id: string;
  vendor: string;
  displayName: string;
  scope: "premise" | "company" | "global";
  enabled: boolean;
  adapterKnown: boolean;
  capabilities: Capabilities | null;
  hasCredentials: boolean;
  credential: { fields: string[]; hints: Record<string, string> } | null;
  lastOkAt: string | null;
  lastErrorCode: string | null;
  lastErrorAt: string | null;
}

interface Props {
  premiseId: string;
}

/** Codes to sentences. CMS banners print `err.message`, which for an API
 *  refusal is the CODE — a raw snake_case identifier reaching an operator is a
 *  known repeat offence in this codebase. */
const REFUSAL_COPY: Record<string, string> = {
  no_connection: "No integration is bound to this hotel.",
  capability_unavailable: "This integration cannot do that.",
  not_implemented: "This vendor is named but not implemented yet.",
  breaker_open:
    "Paused after repeated failures. It will retry automatically shortly.",
  credential_unavailable: "No credentials are stored for this connection.",
  credential_unreadable:
    "The stored credentials could not be read. Re-enter them.",
  unreachable: "The vendor could not be reached.",
  vendor_refused: "The vendor rejected the request.",
  unmapped: "A charge category has no code mapped for this hotel.",
  unknown_vendor: "This build has no adapter for that vendor.",
};

function refusalCopy(code: string | null): string {
  if (!code) return "";
  return REFUSAL_COPY[code] ?? `The integration refused: ${code}`;
}

export default function LinkConnectionsPanel({ premiseId }: Props) {
  const [adapters, setAdapters] = useState<AdapterStatus[] | null>(null);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [vendor, setVendor] = useState("");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([
        fetch("/api/foyer/link/adapters", { credentials: "include" }).then(
          (r) => r.json()
        ),
        fetch(`/api/foyer/premise/${premiseId}/link/connections`, {
          credentials: "include",
        }).then((r) => r.json()),
      ]);
      setAdapters(a.adapters ?? []);
      setConnections(c.connections ?? []);
    } catch {
      setError("Could not load integrations.");
      setAdapters([]);
    }
  }, [premiseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const chosen = adapters?.find((a) => a.vendor === vendor) ?? null;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/foyer/premise/${premiseId}/link/connections`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ vendor, config, credentials }),
        }
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        setError(refusalCopy(body.error ?? null) || "Could not save.");
        return;
      }
      setCredentials({});
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function test(id: string) {
    setBusy(true);
    setTestResult(null);
    try {
      const res = await fetch(
        `/api/foyer/premise/${premiseId}/link/connections/${id}/test`,
        { method: "POST", credentials: "include" }
      );
      const body = (await res.json()) as { ok: boolean; code: string | null };
      setTestResult(
        body.ok
          ? "Connected. The vendor answered."
          : refusalCopy(body.code) || "The test failed."
      );
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (adapters === null) {
    return (
      <Card variant="outlined">
        <CardContent>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">Integrations</Typography>

          {adapters.length === 0 && (
            <Alert severity="info" data-testid="link-no-adapters">
              This build has no integrations available. Charges are recorded in
              Foyer for manual reconciliation.
            </Alert>
          )}

          {connections.map((c) => (
            <Box
              key={c.id}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
              data-testid={`link-connection-${c.vendor}`}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">{c.displayName}</Typography>
                  {c.scope !== "premise" && (
                    <Chip size="small" label="From the chain" />
                  )}
                  {!c.enabled && <Chip size="small" label="Off" />}
                  {c.capabilities?.demo && (
                    <Chip
                      size="small"
                      color="warning"
                      label="Demo — no real money"
                    />
                  )}
                </Stack>

                {/* A connection bound to a vendor this build cannot talk to is
                    REPORTED, not hidden: it is exactly what an operator needs
                    to see, and hiding it is how the old page lied. */}
                {!c.adapterKnown && (
                  <Alert severity="error">
                    This hotel is bound to “{c.vendor}”, which this build has no
                    adapter for. Nothing will be sent.
                  </Alert>
                )}

                {c.capabilities && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {c.capabilities.postCharge && (
                      <Chip size="small" variant="outlined" label="Charges" />
                    )}
                    {c.capabilities.readStays && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Guest stays"
                      />
                    )}
                    {c.capabilities.readMenu && (
                      <Chip size="small" variant="outlined" label="Menu" />
                    )}
                    {/* Stated out loud rather than left to be assumed: a vendor
                        that cannot tell us whether a room may be charged means
                        the check is unavailable, not that it passed. */}
                    {!c.capabilities.chargeableFlag && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="No no-post check"
                      />
                    )}
                  </Stack>
                )}

                {c.credential && (
                  <Typography variant="caption" color="text.secondary">
                    Credentials stored:{" "}
                    {c.credential.fields
                      .map((f) => `${f} ${c.credential?.hints[f] ?? ""}`)
                      .join(", ") || "unreadable — re-enter them"}
                  </Typography>
                )}

                {c.lastErrorCode && (
                  <Alert severity="warning">
                    {refusalCopy(c.lastErrorCode)}
                    {c.lastErrorAt
                      ? ` (${new Date(c.lastErrorAt).toLocaleString()})`
                      : ""}
                  </Alert>
                )}

                <Box>
                  <Button
                    size="small"
                    disabled={busy || !c.adapterKnown}
                    onClick={() => void test(c.id)}
                    data-testid={`link-test-${c.vendor}`}
                  >
                    Test connection
                  </Button>
                </Box>
              </Stack>
            </Box>
          ))}

          {testResult && <Alert severity="info">{testResult}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {adapters.length > 0 && (
            <Stack spacing={2}>
              <FormControl size="small" sx={{ maxWidth: 320 }}>
                <InputLabel id="link-vendor-label">Add integration</InputLabel>
                <Select
                  labelId="link-vendor-label"
                  label="Add integration"
                  value={vendor}
                  onChange={(e) => {
                    setVendor(String(e.target.value));
                    setConfig({});
                    setCredentials({});
                  }}
                  data-testid="link-vendor"
                >
                  {/* No hardcoded list. Every option is an adapter this build
                      can actually talk to. */}
                  {adapters.map((a) => (
                    <MenuItem key={a.vendor} value={a.vendor}>
                      {a.displayName}
                      {a.operational ? "" : " — not implemented"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {chosen && !chosen.operational && (
                <Alert severity="warning">
                  {chosen.reason ??
                    "This integration is declared but not implemented."}
                </Alert>
              )}

              {(chosen?.configFields ?? []).map((f) => (
                <TextField
                  key={f.key}
                  size="small"
                  label={f.label}
                  placeholder={f.placeholder}
                  helperText={f.help}
                  value={config[f.key] ?? ""}
                  onChange={(e) =>
                    setConfig({ ...config, [f.key]: e.target.value })
                  }
                />
              ))}

              {(chosen?.credentialFields ?? []).map((f) => (
                <TextField
                  key={f.key}
                  size="small"
                  type="password"
                  label={f.label}
                  helperText={f.help ?? "Stored encrypted. Never shown again."}
                  value={credentials[f.key] ?? ""}
                  onChange={(e) =>
                    setCredentials({ ...credentials, [f.key]: e.target.value })
                  }
                />
              ))}

              {chosen && (
                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={busy}
                    onClick={() => void save()}
                    data-testid="link-save"
                  >
                    Save integration
                  </Button>
                </Box>
              )}
            </Stack>
          )}

          {/* Said out loud rather than left absent: an operator looking for
              on-prem OPERA must find out here, not from a silent failure. */}
          <Typography variant="caption" color="text.secondary">
            On-premise OPERA (FIAS/IFC8) is not supported — it needs a connector
            inside the hotel network.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}
